import { always, mergeDeepRight, prop } from 'ramda'
import { makeObj } from '@v/util/briqRamdaExt.js'
import { reduce } from 'lodash'

export default class ModuleBuilder {
  constructor(apiClient) {
    this.apiClient = apiClient
    this.state = {}
    this.asyncListConfig = null
    this.asyncModelConfig = null
  }

  withState(state) {
    this.state = state
    return this
  }

  withAsyncModel({
    fetchModelName,
    saveModelName,
    url,
    saveUrl,
    fetchResponseMapper,
    upsertResponseMapper,
  }) {
    this.asyncModelConfig = {
      fetchModelName,
      saveModelName,
      url,
      saveUrl,
      fetchResponseMapper,
      upsertResponseMapper,
    }
    return this
  }

  withAsyncList({ name, url, saveUrl, fetchResponseMapper, upsertResponseMapper }) {
    this.asyncListConfig = { name, url, saveUrl, fetchResponseMapper, upsertResponseMapper }
    return this
  }

  build() {
    const thing = makeObj({
      namespaced: always(true),
      state: prop('state'),
      getters: always({}),
      mutations: always({}),
      actions: always({}),
    })(this)
    if (this.asyncListConfig && this.asyncModelConfig) {
      console.error(
        'Module builder does not work with both async list and async model. Please do not use both in the same module',
      )
    }
    if (this.asyncListConfig) {
      const asyncListPart = this.asyncListConfig
        ? buildAsyncListModule({
            ...this.asyncListConfig,
            apiClient: this.apiClient,
          })
        : {}
      return mergeDeepRight(thing, asyncListPart)
    }
    const asyncModelPart = this.asyncModelConfig
      ? buildAsyncModelModule({
          ...this.asyncModelConfig,
          apiClient: this.apiClient,
        })
      : {}
    return mergeDeepRight(thing, asyncModelPart)
  }
}

const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1)

const buildAsyncListModule = ({
  name,
  namePlural,
  url,
  saveUrl,
  fetchResponseMapper,
  upsertResponseMapper,
  apiClient,
}) => {
  if (!name || !url) {
    console.error('buildAsyncListModule needs at least name and url')
    return {}
  }
  const _namePlural = namePlural || `${name}s`
  const _saveUrl = saveUrl || url
  const capitalizedNamePlural = capitalize(_namePlural)
  const capitalizedName = capitalize(name)
  const fetchData = `fetch${capitalizedNamePlural}`
  const setData = `set${capitalizedNamePlural}`
  const isFetching = `isFetching${capitalizedNamePlural}`
  const setIsFetching = `setIsFetching${capitalizedNamePlural}`
  const fetchError = 'fetchError'
  const setFetchError = 'setFetchError'
  const add = `add${capitalizedName}`
  const maybeUpsert = `maybeUpsert${capitalizedName}`
  const upsert = `upsert${capitalizedName}`
  const dataWasFetched = `${_namePlural}WereFetched`
  const setDataWasFetched = `set${capitalizedNamePlural}WereFetched`
  const isSaving = `isSaving${capitalizedName}`
  const setIsSaving = `setIsSaving${capitalizedName}`
  const saveError = 'saveError'
  const setSaveError = 'setSaveError'
  const setter = n => (state, payload) => (state[n] = payload)
  const _fetchResponseMapper = fetchResponseMapper || (x => x)
  const _upsertResponseMapper = upsertResponseMapper || (x => x)
  return {
    state: {
      [_namePlural]: [],
      [dataWasFetched]: false,
      [isFetching]: false,
      [isSaving]: false,
      [fetchError]: null,
      [saveError]: null,
    },
    getters: {
      isPending: state => state[isFetching] || state[isSaving],
    },
    mutations: {
      [setData]: setter(_namePlural),
      [add]: (state, payload) => state[_namePlural].push(payload),
      [setIsFetching]: setter(isFetching),
      [setFetchError]: setter(fetchError),
      [setDataWasFetched]: setter(dataWasFetched),
      [setIsSaving]: setter(isSaving),
      [setSaveError]: setter(saveError),
    },
    actions: {
      [`maybeFetch${capitalizedNamePlural}`]: async ({ state, dispatch }) => {
        if (!state[dataWasFetched] && !state[isFetching]) await dispatch(fetchData)
      },
      [fetchData]: async ({ commit }) => {
        commit(setFetchError, null)
        commit(setIsFetching, true)
        try {
          const response = await apiClient.get(url)
          const model = _fetchResponseMapper(response.data)
          commit(setData, model)
          commit(setDataWasFetched, true)
        } catch (e) {
          commit(setFetchError, e)
        }
        commit(setIsFetching, false)
      },
      [maybeUpsert]: async ({ state, dispatch }, payload) => {
        if (!state[isFetching]) {
          if (!state[dataWasFetched]) {
            await dispatch(fetchData)
          }
          if (!state[fetchError] && !state[isSaving]) {
            await dispatch(upsert, payload)
          }
        }
      },
      [upsert]: async ({ state, dispatch, commit }, payload) => {
        commit(setSaveError, null)
        commit(setIsSaving, true)
        try {
          const response = await apiClient.post(_saveUrl, payload)
          const model = _upsertResponseMapper(response.data, payload)
          commit(add, model)
        } catch (e) {
          commit(setSaveError, e)
        }
        commit(setIsSaving, false)
      },
    },
  }
}

const buildAsyncModelModule = ({
  fetchModelName,
  saveModelName,
  url,
  saveUrl,
  fetchResponseMapper,
  upsertResponseHandler,
  apiClient,
}) => {
  if (!fetchModelName || !url) {
    console.error('buildAsyncModelModule needs at least fetchModelName and url')
    return {}
  }
  const resolveUrl = typeof url === 'function' ? url : () => url
  const _saveUrl = saveUrl || url
  const capitalizedName = capitalize(fetchModelName)
  const _saveName = saveModelName || fetchModelName
  const capitalizedSaveName = capitalize(_saveName)
  const fetchData = `fetch${capitalizedName}`
  const setResponse = 'setResponse'
  const setData = `set${capitalizedName}`
  const isFetching = `isFetching${capitalizedName}`
  const setIsFetching = `setIsFetching${capitalizedName}`
  const fetchError = 'fetchError'
  const setFetchError = 'setFetchError'
  const maybeUpsert = `maybeUpsert${capitalizedSaveName}`
  const upsert = `upsert${capitalizedSaveName}`
  const dataWasFetched = `${fetchModelName}WasFetched`
  const setDataWasFetched = `set${capitalizedName}WasFetched`
  const isSaving = `isSaving${capitalizedSaveName}`
  const setIsSaving = `setIsSaving${capitalizedSaveName}`
  const saveError = 'saveError'
  const setSaveError = 'setSaveError'
  const setter = n => (state, payload) => (state[n] = payload)
  const _fetchResponseMapper = fetchResponseMapper || (x => x)
  const _upsertResponseHandler = upsertResponseHandler || (() => {})
  return {
    state: {
      response: null,
      [fetchModelName]: null,
      [dataWasFetched]: false,
      [isFetching]: false,
      [isSaving]: false,
      [fetchError]: null,
      [saveError]: null,
    },
    getters: {
      isPending: state => state[isFetching] || state[isSaving],
    },
    mutations: {
      [setResponse]: setter('response'),
      [setData]: setter(fetchModelName),
      [setIsFetching]: setter(isFetching),
      [setFetchError]: setter(fetchError),
      [setDataWasFetched]: setter(dataWasFetched),
      [setIsSaving]: setter(isSaving),
      [setSaveError]: setter(saveError),
    },
    actions: {
      [`maybeFetch${capitalizedName}`]: async ({ state, dispatch }, payload) => {
        if (!state[dataWasFetched] && !state[isFetching]) await dispatch(fetchData, payload)
      },
      [fetchData]: async ({ commit }, payload) => {
        commit(setFetchError, null)
        commit(setIsFetching, true)
        try {
          const response = await apiClient.get(resolveUrl(payload))
          commit(setResponse, response)
          const model = _fetchResponseMapper(response.data)
          commit(setData, model)
          commit(setDataWasFetched, true)
        } catch (e) {
          commit(setFetchError, e)
        }
        commit(setIsFetching, false)
      },
      [maybeUpsert]: async ({ state, dispatch }, payload) => {
        if (!state[isFetching]) {
          if (!state[dataWasFetched]) {
            await dispatch(fetchData)
          }
          if (!state[fetchError] && !state[isSaving]) {
            await dispatch(upsert, payload)
          }
        }
      },
      [upsert]: async ({ state, dispatch, commit }, payload) => {
        commit(setSaveError, null)
        commit(setIsSaving, true)
        try {
          const response = await apiClient.post(_saveUrl, payload)
          const model = _upsertResponseHandler({ commit, responseData: response.data, payload })
        } catch (e) {
          commit(setSaveError, e)
        }
        commit(setIsSaving, false)
      },
    },
  }
}
